import React, { useEffect, useMemo, useRef, useState } from 'react';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import { Autocomplete, Box, Button, Grid, Stack, TextField } from '@mui/material';
import { useSnackbar } from 'notistack';
import AddRefundRequest from './AddRefundRequest';
import GlobalDatagrid from '../../components/mui/DataGrid/GlobalDatagrid';
import {
  REFUND_REQUEST_TABLE_HEAD,
  TableHead_Refund_fn,
} from '../../components/datagrid/refund/RefundRequestTableHead';
import { useSelector } from 'react-redux';
import { Icon } from '@iconify/react/dist/iconify.js';
import ViewRefundDetails from './ViewRefundDetails';
import GlobalRefundApprovalReject from './GlobalRefundApprovalReject';
import RequestStats from './RequestStats';
import ToolTip from '../../components/ToolTip/ToolTip';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  getEnrolledRefundRequest,
  getEnrolledRefundRequestFinance,
  getNonEnrolledRefundRequest,
  updateReRequestStatus,
} from '../../redux/features/refundSlice';
import GlobalSelectFieldWithoutFormikNew from '../../components/mui/GlobalSelectFieldWithoutFormikNew';
import FromAndToDatePicker from '../../components/mui/FromAndToDatePicker';
import dayjs from 'dayjs';
import ReasonEdit from './verify_approve/components/ReasonEdit';
import TutorsTable from './verify_approve/components/TutorsTable';
import DetailsAction from './verify_approve/components/DetailsAction';
import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import {
  refundGlobalStatus,
  refundStatusBy,
  refundStatusType,
  typeOfStudent,
} from '../../utils/constants/GlobalStaticData';
import ChatDrawer from '../chatSystem/ChatDrawer';
import { openChat, setRefreashNeeded } from '../../redux/features/chatSlice';
import GlobalTable from '../../components/CustomTableWithPagination/GlobalTable';
import EditBankDetails from './EditBankDetails';

const nonEnrollmentRefundStatus = [
  { id: -1, name: 'All' },
  { id: 0, name: 'Pending' },
  { id: 1, name: 'Approved' },
  { id: 2, name: 'Rejected' },
];

const RefundRequestFinance = ({ finance }) => {
  const { paginationData } = useSelector((state) => state.globalDataGrid);
  const { data: tableRow, meta } = useSelector((state) => state.refund.refundRequestTable);

  const { loading } = useSelector((state) => state.refund);
  const dispatch = useDispatch();

  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 50,
    search: '',
    sort: { field: '', direction: '' },
    checkedRowIds: [],
    frozenColumns: [],
    hiddenColumns: [],
  });

  const [selectedType, setSelectedType] = useState(typeOfStudent[0]);
  const [selectedRoleStatus, setSelectedRoleStatus] = useState({ id: 4, value: 'Finance' });
  const [selectedStatusType, setSelectedStatusType] = useState({ id: -1, value: 'All' });

  const [fromDate, setFromDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD')); //DEFAULT SET TO CURRENT MONTH STARTING DATE TO ENDING DATE
  const [toDate, setToDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [openTutorsTable, setOpenTutorsTable] = useState(false);
  const [openRejectionReason, setOpenRejectionReason] = useState(false);
  const [editReason, setEditReason] = useState('');
  const [openDetails, setOpenDetails] = useState(false);

  const [action, setViewOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [approvalModal, setApprovalModal] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const { open: chatOpenFromRedux, refreashNeeded } = useSelector((state) => state.chat);
  const prevOpenRef = useRef(chatOpenFromRedux);

  const navigate = useNavigate();

  const [openBankDetails, setOpenBankDetails] = useState(false);

  const stableStatusDeps = useMemo(() => {
    const statusBy = selectedRoleStatus?.id;
    const statusType = selectedStatusType?.id;
    if (statusBy == undefined || statusBy == null) {
      setSelectedStatusType({});
    }

    // Only create a dependency when BOTH exist (0 allowed)
    if (statusBy !== undefined && statusType !== undefined) {
      return `${statusBy}-${statusType}`;
    }

    return null;
  }, [selectedRoleStatus?.id, selectedStatusType?.id]);

  const dispatchParams = useMemo(() => {
    return {
      limit: tableState.pageSize,
      page: tableState.page,
      search: tableState.search,
      fromDate,
      toDate,
      status: null,
      type: 2,
      statusBy: selectedRoleStatus?.id === -1 ? null : selectedRoleStatus.id,
      statusType: selectedStatusType?.id === -1 ? null : selectedStatusType.id,
      sortBy: tableState.sort?.field || '',
      sortDirection: tableState.sort?.direction || '',
    };
  }, [
    tableState.page,
    tableState.pageSize,
    tableState.search,
    fromDate,
    toDate,
    stableStatusDeps,
    tableState.sort.field,
    tableState.sort.direction,
  ]);

  useEffect(() => {
    dispatch(
      selectedType?.id ? getNonEnrolledRefundRequest(dispatchParams) : getEnrolledRefundRequestFinance(dispatchParams),
    );
  }, [dispatch, selectedType, dispatchParams]);

  //..........PER ROW REFUND RE-REQUEST TOGGLE (FINANCE)..........//
  const { enqueueSnackbar } = useSnackbar();
  // Ref keeps the latest filters for the refresh, since the columns are memoized on selectedType only
  const dispatchParamsRef = useRef(dispatchParams);
  dispatchParamsRef.current = dispatchParams;

  const handleReRequestToggle = (row, allowReRequest) => {
    dispatch(updateReRequestStatus({ expense_id: row?.expense_id, allow_re_request: allowReRequest })).then((res) => {
      if (res.payload?.status === 'success') {
        enqueueSnackbar(res.payload.message, { variant: 'success' });
        dispatch(getEnrolledRefundRequestFinance(dispatchParamsRef.current));
      } else {
        enqueueSnackbar(res.payload?.message || 'Failed to update re-request status', { variant: 'error' });
      }
    });
  };
  //..............................................................//

  useEffect(() => {
    if (!chatOpen || !selectedRow) return;
    dispatch(
      openChat({
        chatRoomId: selectedRow?.chat_room_id || null,
        groupCreationUsersList: selectedRow?.related_users || [],
        groupName: `Refund - ${selectedRow?.student_name}-${selectedRow?.expense_id}`,
        rowDetails: selectedRow,
      }),
    );
    setChatOpen(false);
  }, [chatOpen]);

  useEffect(() => {
    const justClosed = prevOpenRef.current === true && chatOpenFromRedux === false;
    if (justClosed && refreashNeeded) {
      dispatch(
        selectedType?.id
          ? getNonEnrolledRefundRequest(dispatchParams)
          : getEnrolledRefundRequestFinance(dispatchParams),
      );
      dispatch(setRefreashNeeded(false));
    }
    prevOpenRef.current = chatOpenFromRedux;
  }, [chatOpenFromRedux, dispatchParams, selectedType, refreashNeeded, dispatch]);

  const GLOBAL_DIALOGUE = [
    {
      open: Boolean(openTutorsTable),
      setOpen: setOpenTutorsTable,
      title: 'Tutors',
      content: <TutorsTable ids={openTutorsTable} />,
    },
    {
      open: openRejectionReason,
      title: 'Bank Account Details',
      setOpen: setOpenRejectionReason,
      content: (
        <ReasonEdit
          setOpen={setOpenTutorsTable}
          non={Boolean(selectedType?.id)}
          dispatchParams={dispatchParams}
          submitDispatch={selectedType?.id ? getNonEnrolledRefundRequest : getEnrolledRefundRequest}
          open={openRejectionReason}
        />
      ),
    },
    {
      open: Boolean(openDetails),
      setOpen: setOpenDetails,
      title: 'Details',
      content: (
        <DetailsAction
          action
          data={openDetails}
          setOpen={setOpenDetails}
          dispatchParams={dispatchParams}
          submitDispatch={getNonEnrolledRefundRequest}
        />
      ),
    },
  ];

  const columns = useMemo(
    () =>
      TableHead_Refund_fn({
        setOpenTutorsTable,
        setOpenRejectionReason,
        selectedType: selectedType?.id,
        setOpenDetails,
        setApprovalModal,
        setSelectedRow,
        setEditReason,
        setOpenBankDetails,
        approvalOrVerify: true,
        setChatOpen,
        isFinance: true,
        onReRequestToggle: handleReRequestToggle,
      }),
    [selectedType],
  );

  return (
    <GlobalWrapper title={'Refund Request'}>
      <Box sx={{ width: '100%' }}>
        <Box>
          <RequestStats
            requestStats={meta}
            loading={loading}
            finance={finance}
            filters={{
              fromDate,
              toDate,
              search: tableState.search,
              statusBy: selectedRoleStatus?.id,
              statusType: selectedStatusType?.id,
            }}
          />
        </Box>
        <Grid container spacing={2} my={1}>
          <Grid item xs={12} md={6} lg={4}>
            <FromAndToDatePicker
              disabled={false}
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
              fullWidth={true}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={2.6}>
            <GlobalSelectFieldWithoutFormikNew
              keys={{ itemId: 'id', itemValue: 'name' }}
              data={typeOfStudent}
              label={'Student Type'}
              fullWidth={true}
              selectedDetails={selectedType}
              setChange={setSelectedType}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={2.6}>
            <GlobalSelectFieldWithoutFormikNew
              disabled={selectedType?.id == 1}
              keys={{ itemId: 'id', itemValue: 'name' }}
              data={[{ id: -1, name: 'All' }, ...refundStatusBy]}
              label={'Role Wise Filter'}
              fullWidth={true}
              selectedDetails={selectedRoleStatus}
              setChange={setSelectedRoleStatus}
              clearButton={true}
              onClear={() => {
                setSelectedRoleStatus({ id: -1, value: 'All' });
                setSelectedStatusType({ id: -1, value: 'All' });
              }}
            />
          </Grid>

          <Grid item xs={12} md={6} lg={2.6}>
            <GlobalSelectFieldWithoutFormikNew
              disabled={!selectedRoleStatus?.id || selectedType?.id == 1}
              keys={{ itemId: 'id', itemValue: 'name' }}
              data={[
                { id: -1, name: 'All' },
                ...refundStatusType,
                ...(selectedRoleStatus?.id === 4 ? [{ id: 3, name: 'On Hold' }] : []),
              ]}
              label={'Role Wise Status'}
              fullWidth={true}
              selectedDetails={selectedStatusType}
              setChange={setSelectedStatusType}
              // clearButton={true}
            />
          </Grid>
        </Grid>
        <Box mb={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<Icon icon="lsicon:calendar-filled" />}
            onClick={() => navigate('/my-workspace/scheduled-refund-payments')}
          >
            Scheduled Payments
          </Button>
        </Box>

        {/* <GlobalDatagrid
          tableHead={columns}
          rows={tableRow ?? []}
          meta={meta?.totalRecords || 0}
          loading={loading}
          rowUniqueId="sl_no"
          rowClick={false}
        /> */}
        <GlobalTable
          tableState={tableState}
          setTableState={setTableState}
          headers={columns}
          rows={tableRow ?? []}
          meta={meta}
          loading={loading}
          rowUniqueId="sl_no"
          exportUrl={
            selectedType?.id
              ? 'refund-management/get-non-enrolled-refund-requests'
              : 'refund-management/get-requests-for-finance'
          }
          exportParams={{
            page: 1,
            limit: 'no',
            search: dispatchParams?.search || '',
            statusBy: dispatchParams?.statusBy || null,
            statusType: dispatchParams?.statusType || null,
            fromDate: dispatchParams?.fromDate || null,
            toDate: dispatchParams?.toDate || null,
            status: null,
            type: 2,
          }}
        />
        {/* {action && selectedRow && <ViewRefundDetails open={action} setOpen={setViewOpen} row={selectedRow} />} */}
        {approvalModal && (
          <GlobalRefundApprovalReject
            open={approvalModal}
            setOpen={setApprovalModal}
            rowData={selectedRow}
            dispatchParams={dispatchParams}
            loading={loading}
            action={'finance'}
            apiDispatchAfterSuccess={getEnrolledRefundRequestFinance}
          />
        )}

        <EditBankDetails
          open={openBankDetails}
          setOpen={setOpenBankDetails}
          rowData={selectedRow}
          selectedType={selectedType?.id}
          onSuccess={() => {
            dispatch(
              selectedType?.id
                ? getNonEnrolledRefundRequest(dispatchParams)
                : getEnrolledRefundRequestFinance(dispatchParams),
            );
          }}
        />
        {GLOBAL_DIALOGUE.map(({ open, content, setOpen, title }, i) => (
          <GlobalDialog
            key={i}
            maxWidth={'100%'}
            content={content}
            open={open}
            setOpen={setOpen}
            closeButton={true}
            title={title}
          />
        ))}
      </Box>
    </GlobalWrapper>
  );
};

export default RefundRequestFinance;
